import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from "@mui/joy";
import {
  RestartAlt as ResetIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { Navigate } from "react-router-dom";
import {
  OrgThemeBackgroundLevels,
  OrgThemeBackgrounds,
  OrgThemeColors,
  OrgThemeConfig,
  OrgThemeMode,
  OrgThemePalette,
  OrgThemeSettings,
  PaletteShades,
} from "../common/types";
import {
  useDeleteOrgLogo,
  useGetOrgBranding,
  useReadUser,
  useResetOrgBranding,
  useUpdateOrgBranding,
  useUploadOrgLogo,
} from "../services/api";
import { useOrgTheme } from "../providers/OrgThemeProvider";
import { defaultColors } from "../theme/themes/defaultTheme";
import { buildOrgTheme, buildCssVarBlock } from "../theme/buildOrgTheme";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import PageLoading from "../components/PageLoading";
import PageError from "../components/PageError";
import PaletteEditor from "../components/Organization/Branding/PaletteEditor";
import BackgroundEditor from "../components/Organization/Branding/BackgroundEditor";
import BackgroundLevelsEditor from "../components/Organization/Branding/BackgroundLevelsEditor";
import ColorsEditor from "../components/Organization/Branding/ColorsEditor";
import LogoUpload from "../components/Organization/Branding/LogoUpload";
import BrandingSettings from "../components/Organization/Branding/BrandingSettings";
import ThemePreviewPanel from "../components/Organization/Branding/ThemePreviewPanel";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEFAULT_SETTINGS: OrgThemeSettings = {
  allow_user_theme_override: true,
  default_mode: "system",
};

const emptyMode = (): OrgThemeMode => ({
  palette: { primary: undefined, neutral: undefined, success: undefined, warning: undefined, danger: undefined },
});

const emptyConfig = (): OrgThemeConfig => ({
  light: emptyMode(),
  dark: emptyMode(),
  settings: DEFAULT_SETTINGS,
});

const PALETTES = [
  { key: "primary" as const, label: "Primary" },
  { key: "neutral" as const, label: "Neutral" },
  { key: "success" as const, label: "Success" },
  { key: "warning" as const, label: "Warning" },
  { key: "danger" as const, label: "Danger" },
];

const normalizePaletteShades = (shades: PaletteShades | null | undefined): PaletteShades | undefined =>
  shades ?? undefined;

const normalizeThemeMode = (mode: OrgThemeMode | null | undefined): OrgThemeMode => ({
  ...emptyMode(),
  ...(mode ?? {}),
  palette: {
    ...emptyMode().palette,
    ...(mode?.palette ?? {}),
    primary: normalizePaletteShades(mode?.palette?.primary as PaletteShades | null | undefined),
    neutral: normalizePaletteShades(mode?.palette?.neutral as PaletteShades | null | undefined),
    success: normalizePaletteShades(mode?.palette?.success as PaletteShades | null | undefined),
    warning: normalizePaletteShades(mode?.palette?.warning as PaletteShades | null | undefined),
    danger: normalizePaletteShades(mode?.palette?.danger as PaletteShades | null | undefined),
  },
});

const normalizeThemeConfig = (
  config: OrgThemeConfig | null | undefined,
): OrgThemeConfig => ({
  ...emptyConfig(),
  ...(config ?? {}),
  light: normalizeThemeMode(config?.light),
  dark: normalizeThemeMode(config?.dark),
  settings: config?.settings ?? DEFAULT_SETTINGS,
});

const withCacheBust = (url: string | null, version: number): string | null => {
  if (!url) return null;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${version}`;
};

const revokeObjectUrl = (url: string | null) => {
  if (url?.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OrganizationBranding: React.FC = () => {
  const readUserAPI = useReadUser();
  const getOrgBranding = useGetOrgBranding();
  const updateOrgBranding = useUpdateOrgBranding();
  const resetOrgBranding = useResetOrgBranding();
  const uploadOrgLogo = useUploadOrgLogo();
  const deleteOrgLogo = useDeleteOrgLogo();
  const { updateBranding } = useOrgTheme();

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Remote branding state (what's saved)
  const [savedConfig, setSavedConfig] = useState<OrgThemeConfig | null>(null);
  const [savedLogoLight, setSavedLogoLight] = useState<string | null>(null);
  const [savedLogoDark, setSavedLogoDark] = useState<string | null>(null);

  // Editor local state (dirty until saved)
  const [draftConfig, setDraftConfig] = useState<OrgThemeConfig>(emptyConfig());
  const [draftLogoLight, setDraftLogoLight] = useState<string | null>(null);
  const [draftLogoDark, setDraftLogoDark] = useState<string | null>(null);
  const [pendingLogoLightFile, setPendingLogoLightFile] = useState<File | null>(null);
  const [pendingLogoDarkFile, setPendingLogoDarkFile] = useState<File | null>(null);
  const [pendingDeleteLight, setPendingDeleteLight] = useState(false);
  const [pendingDeleteDark, setPendingDeleteDark] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [resetting, setResetting] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);

  const [modeTab, setModeTab] = useState<"light" | "dark">("light");
  const saveSuccessTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoPreviewVersion = useRef(Date.now());

  const replaceDraftLogo = (mode: "light" | "dark", nextUrl: string | null) => {
    if (mode === "light") {
      setDraftLogoLight((prev) => {
        if (prev !== nextUrl) revokeObjectUrl(prev);
        return nextUrl;
      });
      return;
    }

    setDraftLogoDark((prev) => {
      if (prev !== nextUrl) revokeObjectUrl(prev);
      return nextUrl;
    });
  };

  const syncDraftLogosFromSaved = (lightUrl: string | null, darkUrl: string | null) => {
    logoPreviewVersion.current = Date.now();
    replaceDraftLogo("light", withCacheBust(lightUrl, logoPreviewVersion.current));
    replaceDraftLogo("dark", withCacheBust(darkUrl, logoPreviewVersion.current));
  };

  // -------------------------------------------------------------------------
  // Load user + branding
  // -------------------------------------------------------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const userData = await readUserAPI();
        setUser(userData);
        if (userData?.organization_id) {
          const brandingData = await getOrgBranding(userData.organization_id);
          setSavedConfig(brandingData.theme_config);
          setSavedLogoLight(brandingData.logo_light_url);
          setSavedLogoDark(brandingData.logo_dark_url);
          syncDraftLogosFromSaved(brandingData.logo_light_url, brandingData.logo_dark_url);
          if (brandingData.theme_config) {
            setDraftConfig(normalizeThemeConfig(brandingData.theme_config));
          }
        }
      } catch (e: any) {
        setError(e.message ?? "Failed to load branding");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    revokeObjectUrl(draftLogoLight);
    revokeObjectUrl(draftLogoDark);
  }, [draftLogoDark, draftLogoLight]);

  // -------------------------------------------------------------------------
  // Draft mutations
  // -------------------------------------------------------------------------
  const mutatePalette = (
    modeKey: "light" | "dark",
    paletteKey: keyof OrgThemePalette,
    value: PaletteShades | undefined,
  ) => {
    setDraftConfig((prev) => ({
      ...prev,
      [modeKey]: {
        ...prev[modeKey],
        palette: { ...prev[modeKey]?.palette, [paletteKey]: value },
      },
    }));
    setIsDirty(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const mutateSettings = (settings: OrgThemeSettings) => {
    setDraftConfig((prev) => ({ ...prev, settings }));
    setIsDirty(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const mutateBackgrounds = (modeKey: "light" | "dark", backgrounds: OrgThemeBackgrounds) => {
    setDraftConfig((prev) => ({
      ...prev,
      [modeKey]: { ...prev[modeKey], backgrounds },
    }));
    setIsDirty(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const mutateLevels = (modeKey: "light" | "dark", levels: OrgThemeBackgroundLevels) => {
    setDraftConfig((prev) => ({
      ...prev,
      [modeKey]: { ...prev[modeKey], levels },
    }));
    setIsDirty(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const mutateColors = (modeKey: "light" | "dark", colors: OrgThemeColors) => {
    setDraftConfig((prev) => ({
      ...prev,
      [modeKey]: { ...prev[modeKey], colors },
    }));
    setIsDirty(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  // -------------------------------------------------------------------------
  // Save / Reset
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!user?.organization_id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateOrgBranding(user.organization_id, draftConfig);

      if (pendingLogoLightFile) {
        await uploadOrgLogo(user.organization_id, "light", pendingLogoLightFile);
      } else if (pendingDeleteLight && savedLogoLight) {
        await deleteOrgLogo(user.organization_id, "light");
      }

      if (pendingLogoDarkFile) {
        await uploadOrgLogo(user.organization_id, "dark", pendingLogoDarkFile);
      } else if (pendingDeleteDark && savedLogoDark) {
        await deleteOrgLogo(user.organization_id, "dark");
      }

      const result = await getOrgBranding(user.organization_id);
      setSavedConfig(result.theme_config);
      setSavedLogoLight(result.logo_light_url);
      setSavedLogoDark(result.logo_dark_url);
      setDraftConfig(normalizeThemeConfig(result.theme_config));
      syncDraftLogosFromSaved(result.logo_light_url, result.logo_dark_url);
      setPendingLogoLightFile(null);
      setPendingLogoDarkFile(null);
      setPendingDeleteLight(false);
      setPendingDeleteDark(false);
      setIsDirty(false);
      setSaveSuccess(true);
      updateBranding(result);
      if (saveSuccessTimer.current) clearTimeout(saveSuccessTimer.current);
      saveSuccessTimer.current = setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e: any) {
      setSaveError(e.message ?? "Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!resetConfirm) {
      setResetConfirm(true);
      return;
    }
    if (!user?.organization_id) return;
    setResetting(true);
    try {
      await resetOrgBranding(user.organization_id);
      setSavedConfig(null);
      setSavedLogoLight(null);
      setSavedLogoDark(null);
      setDraftConfig(emptyConfig());
      replaceDraftLogo("light", null);
      replaceDraftLogo("dark", null);
      setPendingLogoLightFile(null);
      setPendingLogoDarkFile(null);
      setPendingDeleteLight(false);
      setPendingDeleteDark(false);
      setIsDirty(false);
      setSaveSuccess(false);
      setResetConfirm(false);
      updateBranding({ theme_config: null, logo_light_url: null, logo_dark_url: null });
    } catch (e: any) {
      setSaveError(e.message ?? "Failed to reset branding");
    } finally {
      setResetting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Logo handlers
  // -------------------------------------------------------------------------
  const handleLogoUpload = (mode: "light" | "dark", file: File) => {
    const objectUrl = URL.createObjectURL(file);

    if (mode === "light") {
      setPendingLogoLightFile(file);
      setPendingDeleteLight(false);
    } else {
      setPendingLogoDarkFile(file);
      setPendingDeleteDark(false);
    }

    replaceDraftLogo(mode, objectUrl);
    setIsDirty(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleLogoDelete = (mode: "light" | "dark") => {
    if (mode === "light") {
      setPendingLogoLightFile(null);
      setPendingDeleteLight(savedLogoLight !== null);
    } else {
      setPendingLogoDarkFile(null);
      setPendingDeleteDark(savedLogoDark !== null);
    }

    replaceDraftLogo(mode, null);
    setIsDirty(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  // -------------------------------------------------------------------------
  // Guards
  // -------------------------------------------------------------------------
  if (loading) return <PageLoading />;
  if (error) return <PageError>{error}</PageError>;
  if (!user) return <PageError>User not found</PageError>;
  if (!user.organization_id || user.role_in_org !== "admin") {
    return <Navigate to="/" replace />;
  }

  // -------------------------------------------------------------------------
  // Live preview — build a complete org theme from draftConfig (so all variant
  // tokens like softBg, plainHoverBg, etc. are derived automatically) and
  // inject it as a scoped <style> block.  Using ".branding-preview" (specificity
  // 0,2,0) beats html[data-joy-color-scheme] (0,1,1) so the preview variables
  // always win within their container.
  // -------------------------------------------------------------------------
  const previewCssString = buildCssVarBlock(buildOrgTheme(draftConfig), ".branding-preview");

  const hasSavedBranding =
    savedConfig !== null || savedLogoLight !== null || savedLogoDark !== null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Branding & Theme</Typography>
        <Typography level="body-sm" color="neutral">
          Customise your organisation's colour palette, logos, and display settings.
        </Typography>
      </PageHeader>

      {/* Two-column layout: editor left, preview right */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 340px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* ---------------------------------------------------------------- */}
        {/* Left: Editor                                                      */}
        {/* ---------------------------------------------------------------- */}
        <Stack spacing={3}>
          {/* Save/Reset toolbar */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<SaveIcon />}
                  onClick={handleSave}
                  loading={saving}
                  disabled={!isDirty || saving || resetting}
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  color={resetConfirm ? "danger" : "neutral"}
                  startDecorator={<ResetIcon />}
                  onClick={handleReset}
                  loading={resetting}
                  disabled={resetting || saving || (!hasSavedBranding && !isDirty)}
                >
                  {resetConfirm ? "Confirm Reset to Default" : "Reset to Default"}
                </Button>
                {resetConfirm && (
                  <Button
                    variant="plain"
                    color="neutral"
                    size="sm"
                    onClick={() => setResetConfirm(false)}
                  >
                    Cancel
                  </Button>
                )}
                {isDirty && (
                  <Chip size="sm" variant="soft" color="warning">
                    Unsaved changes
                  </Chip>
                )}
                {saveSuccess && (
                  <Chip size="sm" variant="soft" color="success">
                    Saved!
                  </Chip>
                )}
              </Box>
              {saveError && (
                <Alert color="danger" variant="soft" size="sm" sx={{ mt: 2 }}>
                  {saveError}
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Colour palettes */}
          <Card>
            <CardContent>
              <Typography level="h3" sx={{ mb: 1 }}>
                Colour Palettes
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Enter your brand colour and click Generate to build the full 9-shade scale.
                Expand each palette to fine-tune individual shades.
              </Typography>

              <Tabs
                value={modeTab}
                onChange={(_e, v) => setModeTab(v as "light" | "dark")}
                sx={{ mb: 2 }}
              >
                <TabList>
                  <Tab value="light">Light Mode</Tab>
                  <Tab value="dark">
                    Dark Mode
                    {draftConfig.dark?.palette &&
                      Object.values(draftConfig.dark.palette).some(Boolean) && (
                        <Chip size="sm" variant="soft" color="primary" sx={{ ml: 1 }}>
                          custom
                        </Chip>
                      )}
                  </Tab>
                </TabList>

                {(["light", "dark"] as const).map((mKey) => (
                  <TabPanel key={mKey} value={mKey} sx={{ px: 0 }}>
                    {mKey === "dark" && !Object.values(draftConfig.dark?.palette ?? {}).some(Boolean) && (
                      <Alert variant="soft" color="neutral" size="sm" sx={{ mb: 2 }}>
                        Dark mode is using the base build-time theme. Add palette overrides below to
                        customise it independently.
                      </Alert>
                    )}
                    <Stack spacing={3} divider={<Divider />}>
                      {PALETTES.map(({ key, label }) => (
                        <PaletteEditor
                          key={key}
                          label={label}
                          value={draftConfig[mKey]?.palette?.[key]}
                          onChange={(shades) => mutatePalette(mKey, key, shades)}
                          disabled={saving || resetting}
                          defaultShades={defaultColors[key]}
                        />
                      ))}
                      <Box>
                        <Typography level="title-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Background Colours
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                          Override the default page and card surface background colours for this
                          mode. Leave blank to inherit the theme default.
                        </Typography>
                        <BackgroundEditor
                          value={draftConfig[mKey]?.backgrounds ?? {}}
                          onChange={(bg) => mutateBackgrounds(mKey, bg)}
                          disabled={saving || resetting}
                          mode={mKey}
                        />
                      </Box>

                      <Box>
                        <Typography level="title-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Background Levels
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                          Override elevated surface tints used for hover states, selected rows, and
                          nested panels. Enter a base colour and click Generate to create all three
                          levels. Leave blank to inherit the theme default.
                        </Typography>
                        <BackgroundLevelsEditor
                          value={draftConfig[mKey]?.levels ?? {}}
                          onChange={(lv) => mutateLevels(mKey, lv)}
                          disabled={saving || resetting}
                          mode={mKey}
                        />
                      </Box>

                      <Box>
                        <Typography level="title-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Colour Overrides
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                          Override individual semantic colours for text and borders. Leave blank to
                          inherit the theme default.
                        </Typography>
                        <ColorsEditor
                          value={draftConfig[mKey]?.colors ?? {}}
                          onChange={(cl) => mutateColors(mKey, cl)}
                          disabled={saving || resetting}
                          mode={mKey}
                        />
                      </Box>
                    </Stack>
                  </TabPanel>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Logos */}
          <Card>
            <CardContent>
              <Typography level="h3" sx={{ mb: 1 }}>
                Logos
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Upload separate logos for light and dark colour modes. PNG, JPG, SVG or WebP,
                max 2 MB.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <LogoUpload
                  mode="light"
                  currentUrl={draftLogoLight}
                  onUpload={(f) => handleLogoUpload("light", f)}
                  onDelete={() => handleLogoDelete("light")}
                  disabled={saving || resetting}
                />
                <LogoUpload
                  mode="dark"
                  currentUrl={draftLogoDark}
                  onUpload={(f) => handleLogoUpload("dark", f)}
                  onDelete={() => handleLogoDelete("dark")}
                  disabled={saving || resetting}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Settings */}
          <Card>
            <CardContent>
              <Typography level="h3" sx={{ mb: 2 }}>
                Display Settings
              </Typography>
              <BrandingSettings
                value={draftConfig.settings}
                onChange={mutateSettings}
                disabled={saving || resetting}
              />
            </CardContent>
          </Card>
        </Stack>

        {/* ---------------------------------------------------------------- */}
        {/* Right: Live preview (sticky)                                      */}
        {/* ---------------------------------------------------------------- */}
        <Box sx={{ position: { lg: "sticky" }, top: { lg: 24 } }}>
          <Card>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2, fontWeight: 600 }}>
                Live Preview
              </Typography>
              <Typography level="body-xs" color="neutral" sx={{ mb: 2 }}>
                Updates as you edit. Previewing {modeTab} mode.
              </Typography>

              {/* Scoped style block: .branding-preview beats html[data-joy-color-scheme]
                  in specificity so all draft tokens (including variant tokens) apply. */}
              <style dangerouslySetInnerHTML={{ __html: previewCssString }} />
              <Box
                className="branding-preview"
                data-joy-color-scheme={modeTab}
              >
                <Box sx={{ bgcolor: "background.body", borderRadius: 0, p: 1.5 }}>
                  <Card size="sm">
                    <CardContent>
                      <ThemePreviewPanel />
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Page>
  );
};

export default OrganizationBranding;
